import {
	colors,
	colorUtility,
	RiFilePaperLine,
	RiLinkM,
	RiPencilLine,
	RiSquareFill
} from "@itandi/itandi-bb-ui";
import { Chip } from "@itandi/itandi-bb-ui/Chip";
import { Flex } from "@itandi/itandi-bb-ui/Flex";
import { Label } from "@itandi/itandi-bb-ui/Label";
import { Td } from "@itandi/itandi-bb-ui/Td";
import { TextLink } from "@itandi/itandi-bb-ui/TextLink";
import { Tr } from "@itandi/itandi-bb-ui/Tr";
import { ConstructionEndDateCell } from "~/components/common/deals/index/ConstructionEndDateCell";
import { DateCell } from "~/components/common/deals/index/DateCell";
import { DealInspectionDateCell } from "~/components/common/deals/index/DealInspectionDateCell";
import { DealTypeChip } from "~/components/common/parts/chip/DealTypeChip";
import { ContentCounter } from "~/components/deprecated/ContentCounter";
import { StatusIcon } from "~/components/deprecated/StatusIcon";
import type { DealIndexClient } from "~/models/Deal";
import { formatPropertyName } from "~/models/Deal";
import type { ItandiAccount } from "~/models/ItandiAccount";
import { statusColor } from "~/models/Status";
import { PATH as CLIENT_DEAL_PATH } from "~/pages/deals/[id]";
import { PATH as PROPERTY_DEAL_PATH } from "~/pages/property_deals/[id]/index";
import { truncate } from "~/utils/StringUtil";

type Props = {
	deal: DealIndexClient;
	session: ItandiAccount;
	statusNames: Record<string, string>;
};

export function DealTableRow({ deal, session, statusNames }: Props) {
	const unread = deal.unreadMessageCount > 0 || deal.displayUnread;
	const unreadChipLabel =
		deal.unreadMessageCount > 0 ? `未読${deal.unreadMessageCount}` : "未読";

	const propertyDealPath = () => {
		if (session.company?.useV2DealDetail) {
			return `${PROPERTY_DEAL_PATH(deal.propertyDealId)}?dealId=${deal.id}`;
		}
		return PROPERTY_DEAL_PATH(deal.propertyDealId);
	};

	return (
		<Tr>
			<Td textAlign="Left" verticalAlign="Top">
				<Flex alignItems="flex-start" direction="column" gap={2}>
					<Chip
						Icon={RiSquareFill}
						iconColor={statusColor[deal.statusId]}
						label={statusNames[deal.statusId]}
					/>
					{unread ? (
						<Chip
							background={colors["semantic-negative"]}
							borderColor={null}
							label={unreadChipLabel}
							textColor={colors["itandi-white"]}
						/>
					) : null}
					{deal.hasTemporaryDealCancel ? (
						<Chip
							background={colors["semantic-negative"]}
							borderColor={null}
							Icon={RiFilePaperLine}
							iconColor={colors["itandi-white"]}
							label="取下申請中"
							textColor={colors["itandi-white"]}
						/>
					) : null}
					{deal.hasRequestEstimation ? (
						<Chip
							background={colors["expressive-aqua-dark"]}
							borderColor={null}
							Icon={RiFilePaperLine}
							iconColor={colors["itandi-white"]}
							label="見積申請あり"
							textColor={colors["itandi-white"]}
						/>
					) : null}
					{deal.hasDraftEstimationByCompany ? (
						<Chip
							background={colors["expressive-yellow-dark"]}
							borderColor={null}
							Icon={RiPencilLine}
							iconColor={colors["itandi-white"]}
							label="見積編集中"
							textColor={colors["itandi-white"]}
						/>
					) : null}
					{deal.hasRequestEstimationByCompany ? (
						<Chip
							background={colors["expressive-blue"]}
							borderColor={null}
							Icon={RiFilePaperLine}
							iconColor={colors["itandi-white"]}
							label="見積申請中"
							textColor={colors["itandi-white"]}
						/>
					) : null}
				</Flex>
			</Td>
			<Td textAlign="Left" verticalAlign="Top">
				<Flex gap={2}>
					<DealTypeChip
						dealSubtype={deal.dealSubtype}
						dealType={deal.dealType}
					/>
					{deal.relatedDealCount > 0 && (
						<Chip
							background={colorUtility(colors["expressive-aqua-dark"]).alpha(
								0.1
							)}
							borderColor={colors["expressive-aqua-dark"]}
							fontWeight="bold"
							Icon={RiLinkM}
							iconColor={colors["expressive-aqua-dark"]}
							label={`関連${deal.relatedDealCount}`}
							onClick={(): void => {
								window.open(propertyDealPath());
							}}
							textColor={colors["expressive-aqua-dark"]}
						/>
					)}
				</Flex>
				<Flex direction="column" gap={2}>
					<TextLink
						color="link"
						href={CLIENT_DEAL_PATH(deal.id)}
						target="_blank"
						weight="bold"
					>
						{formatPropertyName(deal, 30)}
					</TextLink>
					<Label color="subtle">{truncate(deal.address, 35)}</Label>
					{deal.tenantName ? (
						<Label color="subtle">
							{`${truncate(deal.tenantName, 15)} 様`}
						</Label>
					) : null}
				</Flex>
			</Td>
			<Td textAlign="Left" verticalAlign="Top">
				{deal.uncompletedTodoCount > 0 && (
					<ContentCounter
						count={deal.uncompletedTodoCount}
						onClick={(): void => {
							window.open(CLIENT_DEAL_PATH(deal.id));
						}}
						variant="Todo"
					/>
				)}
			</Td>
			<Td textAlign="Left" verticalAlign="Top">
				<Label>{deal.companyStoreSetting.name}</Label>
				<br />
				<Label color="subtle" size="xs">
					{truncate(deal.staff.name, 35)}
				</Label>
			</Td>
			<Td textAlign="Left" verticalAlign="Top">
				{deal.dealVendorCompanies.length === 0 ? (
					<>
						<Label>施工会社未指定</Label>
						<StatusIcon variant="Alert" />
					</>
				) : (
					<Flex direction="column" gap={2}>
						<Label>
							{deal.dealVendorCompanies[0].companyVendor?.name ?? ""}
						</Label>
						{deal.dealVendorCompanies[0].user ? (
							<Label color="subtle" size="xs">
								{truncate(deal.dealVendorCompanies[0].user.name, 35)}
							</Label>
						) : (
							deal.dealVendorCompanies[0].vendorCompany && (
								<Flex>
									<Label size="xs">担当者設定なし</Label>
									<StatusIcon variant="Alert" />
								</Flex>
							)
						)}
					</Flex>
				)}
			</Td>
			<DateCell date={deal.updatedDate} displayTime />
			<DateCell date={deal.terminationDate} />
			<DealInspectionDateCell deal={deal} />
			<DateCell date={deal.surveyDate} />
			<DateCell date={deal.sendEstimateDate} />
			<DateCell date={deal.orderDate} />
			<DateCell date={deal.constructionStartDate} />
			<ConstructionEndDateCell
				constructionEndDate={deal.constructionEndDate}
				constructionEndDateStatus={deal.constructionEndDateStatus}
			/>
			<DateCell date={deal.constructionReportedDate} />
			<DateCell date={deal.completedDate} />
			<DateCell date={deal.preferredDate} />
			<DateCell date={deal.createdAt} />
		</Tr>
	);
}
